import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import clsx from 'clsx';

import PrimaryButton from 'shared/components/ui/PrimaryButton/PrimaryButton';
import { ROUTES } from 'shared/constants';
import { RoomStatusEnum } from 'shared/constants/shared-enums';

import ReturnIcon from 'assets/animation/buttons/return.svg?react';
import ControllerIconAccept from 'assets/controller-accept.svg?react';
import ControllerIcon from 'assets/game.svg?react';

export const GameButton = ({
  address,
  userAddress,
  status,
  ourGame,
  chainId,
  roomId,
  loading,
  isError,
  errorMessage,
  isItemHovered,
  handleJoinGame,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isUserOwner = address.toLowerCase() === userAddress?.toLowerCase();
  // isWaitingOpponent means that only one player is in the room
  const isWaitingOpponent = status === RoomStatusEnum.Open;
  const isOpenOwnGame = ourGame && status === RoomStatusEnum.Open;

  const buttonText = useMemo(() => {
    if (ourGame && (isUserOwner || !isWaitingOpponent)) {
      return 'common.return';
    }
    if (isOpenOwnGame) {
      return isError ? errorMessage : 'common.acceptInvite';
    }
    return isError ? errorMessage : 'common.joinGame';
  }, [isError, isOpenOwnGame, errorMessage, ourGame, isUserOwner, isWaitingOpponent]);
  const buttonSvg = useMemo(() => {
    if (ourGame && (isUserOwner || !isWaitingOpponent)) {
      return <ReturnIcon className="h-5 w-5 sm:h-6 sm:w-6" />;
    }
    if (isOpenOwnGame) {
      return <ControllerIconAccept className="h-5 w-5 text-[#4DE47F] sm:h-6 sm:w-6" />;
    }
    return <ControllerIcon className="h-5 w-5 sm:h-6 sm:w-6" />;
  }, [isError, isOpenOwnGame, errorMessage, ourGame, isUserOwner, isWaitingOpponent]);
  const buttonType = useMemo(() => {
    if (ourGame && (isUserOwner || !isWaitingOpponent)) {
      return 'return';
    }
    if (isOpenOwnGame) {
      return 'accept';
    }
    return 'primary';
  }, [isError, isOpenOwnGame, errorMessage, ourGame, isUserOwner, isWaitingOpponent]);
  const handleClick = useCallback(() => {
    if (ourGame && (isUserOwner || !isWaitingOpponent)) {
      navigate(ROUTES.ROOM + `/${chainId}/${Number(roomId)}`);
      return;
    }
    return handleJoinGame();
  }, [chainId, roomId, navigate, ourGame, isUserOwner, isWaitingOpponent, handleJoinGame]);

  if (isItemHovered || loading) {
    return (
      <PrimaryButton
        onClick={handleClick}
        isLoading={loading}
        text={buttonText}
        svg={!isError && buttonSvg}
        className="col-span-4 sm:w-[192px]"
        innerClassName="sm:text-[20px]"
        buttonType={buttonType}
      />
    );
  }

  // Default inactive state
  return (
    <div
      className="align-center col-span-4 flex h-12 items-center justify-center opacity-50 transition-all sm:w-[192px]"
      onClick={handleClick}
    >
      <div
        className={clsx(
          'flex items-center gap-2 text-[14px] font-semibold text-white sm:text-[20px]',
        )}
      >
        {isError ? (
          <span className="sm:text-lg">{errorMessage}</span>
        ) : (
          <>
            {buttonSvg}
            {t(buttonText)}
          </>
        )}
      </div>
    </div>
  );
};
